import React, { useState, useRef } from "react";
import { Editor } from '@tinymce/tinymce-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import AuthService from "../../../api/services/AuthService";
import { alertSuccessMessage, alertErrorMessage } from "../../../customComponent/CustomAlertMessage";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";
const tinymceApiKey = process.env.REACT_APP_TINY_API || 'your-tinymce-api-key';

const Blog = () => {
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [blogLink, setBlogLink] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [blogMedia, setBlogMedia] = useState(null);
  const [content, setContent] = useState("");
  const editorRef = useRef(null);

  // SEO Fields
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [slug, setSlug] = useState('');

  // Image cropping states
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: 2 / 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);


  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*$/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

      if (!allowedImageTypes.includes(file.type)) {
        alertErrorMessage("Only image files (JPEG, PNG, GIF, WEBP) are allowed for blog media");
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setShowCropModal(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const defaultCrop = {
      unit: '%',
      width: 100,
      height: 50,
      x: 0,
      y: 25,
      aspect: 2 / 1
    };
    setCrop(defaultCrop);
    // Set completedCrop to allow default crop without user interaction
    setCompletedCrop({
      unit: '%',
      width: 100,
      height: 50,
      x: 0,
      y: 25
    });
  };

  const getCroppedImg = async () => {
    if (!imgRef.current) {
      alertErrorMessage("Image not loaded properly");
      return;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Use completedCrop if available, otherwise use current crop
    const cropToUse = completedCrop || crop;

    canvas.width = cropToUse.width;
    canvas.height = cropToUse.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      cropToUse.x * scaleX,
      cropToUse.y * scaleY,
      cropToUse.width * scaleX,
      cropToUse.height * scaleY,
      0,
      0,
      cropToUse.width,
      cropToUse.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          alertErrorMessage('Canvas is empty');
          return;
        }
        const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
        resolve(file);
      }, 'image/jpeg', 1);
    });
  };

  const handleCropComplete = async () => {
    const croppedImage = await getCroppedImg();
    if (croppedImage) {
      setBlogMedia(croppedImage);
      setPreviewUrl(URL.createObjectURL(croppedImage));
      setShowCropModal(false);
      setImgSrc('');
      alertSuccessMessage("Image cropped successfully");
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImgSrc('');
    setCrop({ unit: '%', width: 100, aspect: 2 / 1 });
    setCompletedCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChangeIdentity = async (event) => {
    event.preventDefault();
    const fileUploaded = event.target.files[0];

    if (!fileUploaded) return;

    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (!allowedImageTypes.includes(fileUploaded.type)) {
      alertErrorMessage("Only image files (JPEG, PNG, GIF, WEBP) are allowed for blog media");
      event.target.value = "";
      return;
    }

    const img = new Image();
    img.onload = function () {
      if (this.width <= 1024 && this.height <= 512) {
        setBlogMedia(fileUploaded);
      } else {
        alertErrorMessage(`Image size (${this.width} x ${this.height}) exceeds limit (1024 x 512)`);
        event.target.value = "";
      }
    };
    img.src = URL.createObjectURL(fileUploaded);
  };

  const resetInputChange = () => {
    setCategory("");
    setBlogMedia(null);
    setContent("");
    setTitle("");
    setBlogLink("");
    setYoutubeUrl("");
    setPreviewUrl("");
    setImgSrc("");
    setCrop({ unit: '%', width: 100, aspect: 2 / 1 });
    setCompletedCrop(null);
    
    // Reset SEO Fields
    setMetaTitle('');
    setMetaDescription('');
    setMetaKeywords('');
    setSlug('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (editorRef.current) {
      editorRef.current.setContent("");
    }
  }


  const handleAddBanner = async () => {
    // Validate that at least one media type is provided
    if (!blogMedia && !youtubeUrl) {
      alertErrorMessage("Please provide either a blog image or YouTube URL");
      return;
    }

    // Validate blog link format if provided
    const blogLinkRegex = /^(https?:\/\/)[\w\-]+(\.[\w\-]+)+[/#?]?.*$/;
    if (blogLink && !blogLinkRegex.test(blogLink)) {
      alertErrorMessage("Please enter a valid blog link (e.g., https://example.com)");
      return;
    }

    // Validate YouTube URL format if provided
    if (youtubeUrl && !youtubeUrl.includes('youtube.com') && !youtubeUrl.includes('youtu.be')) {
      alertErrorMessage("Please enter a valid YouTube URL");
      return;
    }

    var formData = new FormData();
    formData.append('category', category);
    formData.append('title', title);
    formData.append('content', content);
    formData.append('blogLink', blogLink);
    formData.append('youtubeUrl', youtubeUrl);

    // SEO Fields
    formData.append('metaTitle', metaTitle);
    formData.append('metaDescription', metaDescription);
    formData.append('metaKeywords', metaKeywords);
    formData.append('slug', slug);

    if (blogMedia) {
      formData.append('blogMedia', blogMedia);
    }

    LoaderHelper.loaderStatus(true);
    await AuthService.addBlog(formData).then(async result => {
      LoaderHelper.loaderStatus(false);
      if (result.success) {
        try {
          alertSuccessMessage(result.message);
          resetInputChange();
        } catch (error) {
          alertErrorMessage(error);
        }
      } else {
        const errorMessage = result.message;
        alertErrorMessage(errorMessage);
      }
    });
  }


  return (
    <>
      <div id="layoutSidenav_content">
        <main>
          <header className="page-header page-header-dark bg-gradient-primary-to-secondary pb-10">
            <div className="container-xl px-4">
              <div className="page-header-content pt-4">
                <div className="row align-items-center justify-content-between">
                  <div className="col-auto mt-4">
                    <h1 className="page-header-title">
                      <div className="page-header-icon"><i className="fa fa-image"></i></div>
                      Add New Blog
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </header>
          <div className="container-xl px-4 mt-n10">
            <div className="card mb-4">
              <div className="card-body p-5">
                <form>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-4 form-group">
                        <label className="small mb-1" htmlFor="categorySelect">Category <span className="text-danger">*</span></label>
                        <select
                          className="form-control form-control-solid form-select"
                          id="categorySelect"
                          style={{color:'white'}}
                          name="category"
                          value={category}
                          onChange={(event) => setCategory(event.target.value)}
                        >
                          <option style={{color:'black'}} value="" hidden>Select Blog Category</option>
                          <option style={{color:'black'}} value="News">News</option>
                          <option style={{color:'black'}} value="Analysis">Analysis</option>
                          <option style={{color:'black'}} value="Events">Events</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="small mb-1">Blog Title <span className="text-danger">*</span></label>
                        <input
                          className="form-control form-control-solid"
                          type="text"
                          placeholder="Enter Blog Title"
                          name="title"
                          value={title}
                          onChange={(event) => {
                            const newTitle = event.target.value;
                            setTitle(newTitle);
                            // Auto-generate slug from title
                            const autoSlug = newTitle
                              .toLowerCase()
                              .trim()
                              .replace(/[^a-z0-9\s-]/g, '')
                              .replace(/\s+/g, '-')
                              .replace(/-+/g, '-');
                            setSlug(autoSlug);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="small mb-1">External Blog Link (Optional)</label>
                        <input
                          className="form-control form-control-solid"
                          type="text"
                          placeholder="paste link here"
                          name="blogLink"
                          value={blogLink}
                          onChange={(event) => setBlogLink(event.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="small mb-1">YouTube URL (Optional)</label>
                        <input
                          className="form-control form-control-solid"
                          type="text"
                          placeholder="https://youtube.com/watch?v=..."
                          name="youtubeUrl"
                          value={youtubeUrl}
                          onChange={(event) => setYoutubeUrl(event.target.value)}
                        />
                        <small className="text-muted">Provide YouTube URL or upload an image below</small>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="small mb-1">
                          Wall Image {!youtubeUrl && <span className="text-danger">*</span>}
                          <small className="text-danger ms-1">(Aspect Ratio: 2:1)</small>
                        </label>
                        <div>
                          <input
                            ref={fileInputRef}
                            className="d-none"
                            id="bannerImg"
                            type="file"
                            name="blogMedia"
                            onChange={onSelectFile}
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          />
                          {!previewUrl ? (
                            <button
                              type="button"
                              className="btn btn-primary mb-3"
                              onClick={handleButtonClick}
                            >
                              <i className="fa fa-upload me-2"></i>
                              Upload Blog Wall Image
                            </button>
                          ) : (
                            <div>
                              <p className="small mb-2 fw-bold">Preview:</p>
                              <div className="position-relative d-inline-block">
                                <img
                                  src={previewUrl}
                                  alt="Preview"
                                  style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                              </div>
                              <div className="mt-2">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm me-2"
                                  onClick={() => {
                                    setBlogMedia(null);
                                    setPreviewUrl('');
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = '';
                                    }
                                  }}
                                >
                                  <i className="fa fa-trash me-1"></i>
                                  Remove Image
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  onClick={handleButtonClick}
                                >
                                  <i className="fa fa-sync me-1"></i>
                                  Change Image
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <small className="text-muted">Supported formats: JPEG, JPG, PNG, GIF, WEBP</small>
                      </div>
                    </div>
                    <div className="col-md-6">

                      {youtubeUrl && getYouTubeVideoId(youtubeUrl) && (
                        <div className="mt-3">
                          <p className="small mb-2 fw-bold">YouTube Preview:</p>
                          <div className="ratio ratio-16x9" style={{ maxWidth: '100%' }}>
                            <iframe
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(youtubeUrl)}`}
                              title="YouTube video preview"
                              allowFullScreen
                              style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                            ></iframe>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>




                  <div className="form-group mb-4">
                    <label className="small mb-1">Blog Content <span className="text-danger">*</span></label>
                    <Editor
                      apiKey={tinymceApiKey}
                      onInit={(evt, editor) => editorRef.current = editor}
                      value={content}
                      onEditorChange={(newContent) => setContent(newContent)}
                      init={{
                        height: 400,
                        menubar: true,
                        plugins: [
                          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                          'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | blocks | ' +
                          'bold italic forecolor | alignleft aligncenter ' +
                          'alignright alignjustify | bullist numlist outdent indent | ' +
                          'removeformat | help',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                      }}
                    />
                  </div>

                  {/* SEO Section */}
                  <div className="border-top pt-4 mt-4">
                    <h6 className="mb-3 fw-bold">SEO Details (Search Engine Optimization)</h6>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="small mb-1">SEO Meta Title</label>
                          <input
                            className="form-control form-control-solid"
                            type="text"
                            placeholder="Enter SEO meta title (recommended: 50-60 characters)"
                            value={metaTitle}
                            onChange={(e) => setMetaTitle(e.target.value)}
                            maxLength="60"
                          />
                          <small className="text-muted">{metaTitle.length}/60 characters - Used in search results</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="small mb-1">URL Slug</label>
                          <input
                            className="form-control form-control-solid"
                            type="text"
                            placeholder="blog-url-slug"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                          />
                          <small className="text-muted">SEO-friendly URL (e.g., my-awesome-blog-post)</small>
                        </div>
                      </div>
                    </div>

                    <div className="form-group mb-3">
                      <label className="small mb-1">SEO Meta Description</label>
                      <textarea
                        className="form-control form-control-solid"
                        rows="3"
                        placeholder="Enter SEO meta description (recommended: 150-160 characters)"
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        maxLength="160"
                      ></textarea>
                      <small className="text-muted">{metaDescription.length}/160 characters - Shown in search results preview</small>
                    </div>

                    <div className="form-group mb-4">
                      <label className="small mb-1">Meta Keywords</label>
                      <input
                        className="form-control form-control-solid"
                        type="text"
                        placeholder="keyword1, keyword2, keyword3"
                        value={metaKeywords}
                        onChange={(e) => setMetaKeywords(e.target.value)}
                      />
                      <small className="text-muted">Separate keywords with commas for better SEO indexing</small>
                    </div>
                  </div>

                  <div className="d-flex gap-2 justify-content-center align-items-center">
                    <button
                      className="btn btn-indigo btn-block "
                      type="button"
                      onClick={handleAddBanner}
                      disabled={!category || !title || !content || (!blogMedia && !youtubeUrl)}
                    >
                      Create Blog
                    </button>
                    {/* <button
                      className="btn btn-light"
                      type="button"
                      onClick={resetInputChange}
                    >
                      Reset
                    </button> */}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </main >
      </div >

      {/* Image Crop Modal */}
      {
        showCropModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '90vw' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Crop Blog Wall Image</h5>
                  <button type="button" className="btn-close" onClick={handleCropCancel}></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {imgSrc && (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={2 / 1}
                        style={{ maxWidth: '100%' }}
                      >
                        <img
                          ref={imgRef}
                          alt="Crop preview"
                          src={imgSrc}
                          onLoad={onImageLoad}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '60vh',
                            width: 'auto',
                            height: 'auto',
                            display: 'block'
                          }}
                        />
                      </ReactCrop>
                    </div>
                  )}
                  <small className="text-muted d-block mt-2 text-center" style={{ position: 'absolute', bottom: '60px' }}>
                    Drag to adjust the crop area. The image will be cropped to a 2:1 aspect ratio.
                  </small>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCropCancel}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleCropComplete}>
                    <i className="fa fa-check me-2"></i>OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </>
  )
}

export default Blog;